import { useState } from "react";
import { useTranslation } from "react-i18next";
import LogoHeader from "../../components/header/logoHeader";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import Button from "../../components/common/imageButton";
import { signupUser } from "../../redux/slices/loginSlice";

function Signup() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading } = useSelector((state) => state.auth);

  const [userId, setUserId] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState("");

  // Regular expressions for validation
  const usernameRegex = /^[^\s]+(\s[^\s]+)*$/; // No space at start/end, single space allowed between words
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;// Strong 6-character password

  /**
   * Validates input fields dynamically onChange.
   */
  const validateField = (field, value) => {
    if (field === "userId") {
      if (!value.trim()) return t("signup.error_empty_userid");
      if (value.includes(" ")) return t("signup.error_invalid_userid");
    }
    if (field === "username") {
      if (!value.trim()) return t("signup.error_empty_username");
      if (!usernameRegex.test(value)) return t("signup.error_invalid_username");
    }
    if (field === "password") {
      if (!value) return t("signup.error_empty_password");
      if (!passwordRegex.test(value)) return t("signup.error_invalid_password");
    }
    if (field === "confirmPassword") {
      if (!value) return t("signup.error_empty_confirm_password");
      if (value !== password) return t("signup.error_password_mismatch");
    }
    return "";
  };

  /**
   * Handles onChange and sets validation errors dynamically.
   */
  const handleInputChange = (field, value) => {
    if (field === "userId") setUserId(value);
    if (field === "username") setUsername(value);
    if (field === "password") setPassword(value);
    if (field === "confirmPassword") setConfirmPassword(value);

    // Validate and update error message
    const errorMessage = validateField(field, value);
    setLocalError(errorMessage);
  };

  /**
   * Handles signup form submission.
   */
  const handleSignup = async () => {
    const fields = { userId, username, password, confirmPassword };
  
    for (const [field, value] of Object.entries(fields)) {
      const errorMessage = validateField(field, value);
      if (errorMessage) {
        setLocalError(errorMessage);
        return;
      }
    }
  
    try {
      const payload = { user_id: userId, username, password };
      await dispatch(signupUser(payload)).unwrap();
  
      toast.success(t("signup.signup_success"));
      navigate("/login", { replace: true });
    } catch (error) {
      toast.error(error || "Signup failed. Please try again.");
    }
  };
  

  return (
    <>
      <LogoHeader />
      <div className="flex justify-center items-center mt-5">
        <div className="border-4 border-[#E2A32C] rounded-xl p-5 shadow-lg w-full max-w-md mx-4">
          <h2 className="text-2xl font-semibold mb-8 text-center text-white">
            {t("signup.signup_title")}
          </h2>
          <div className="space-y-4">
            <input
              type="text"
              placeholder={t("signup.user_id_placeholder")}
              value={userId}
              onChange={(e) => handleInputChange("userId", e.target.value)}
              className="w-full px-4 py-2 border border-[#FFBF4D] rounded-lg focus:ring-2 focus:ring-[#FFBF4D] focus:outline-none text-gray-700"
            />

            <input
              type="text"
              placeholder={t("signup.username_placeholder")}
              value={username}
              onChange={(e) => handleInputChange("username", e.target.value)}
              className="w-full px-4 py-2 border border-[#FFBF4D] rounded-lg focus:ring-2 focus:ring-[#FFBF4D] focus:outline-none text-gray-700"
            />

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t("signup.password_placeholder")}
                value={password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                className="w-full px-4 py-2 border border-[#FFBF4D] rounded-lg focus:ring-2 focus:ring-[#FFBF4D] focus:outline-none text-gray-700"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <AiFillEyeInvisible size={20} /> : <AiFillEye size={20} />}
              </button>
            </div>

            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder={t("signup.confirm_password_placeholder")}
                value={confirmPassword}
                onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                className="w-full px-4 py-2 border border-[#FFBF4D] rounded-lg focus:ring-2 focus:ring-[#FFBF4D] focus:outline-none text-gray-700"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <AiFillEyeInvisible size={20} /> : <AiFillEye size={20} />}
              </button>
            </div>
          </div>

          {/* Single Error Message Display */}
          {localError ? (
            <div className="mt-2">
              <p className="text-red-500 text-sm text-center">{localError}</p>
            </div>
          ) : (
            <div className="mt-9">
              <p></p>
            </div>
          )}

          <div className="mt-6">
            <Button
              text={isLoading ? t("loading") : t("signup.signup_button")}
              onClick={handleSignup}
              title={t("signup.signup_title")}
              buttonTitle={t("signup.signup_button")}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center mt-4">
        <img src="images/box.png" alt="Jackpot Box" className="w-36 h-40 mt-4" />
      </div>
    </>
  );
}

export default Signup;
